import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Eye, Tag, User } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import ReactMarkdown from 'react-markdown';
import PageTitle from '../hooks/PageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InsightDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInsight();
  }, [id]);

  const fetchInsight = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/insights/${id}`);
      setInsight(response.data);
    } catch (error) {
      console.error('Error fetching insight:', error);
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-3/4 mb-8"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !insight) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl font-serif text-gray-900 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">The article you're looking for doesn't exist or has been removed.</p>
            <Button onClick={() => navigate('/insights')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Insights
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PageTitle title={insight?.title || 'Article'} />
      {/* Header */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-gray-900 text-white text-sm rounded-full">
              {insight.category}
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-serif text-gray-900 mb-6">
            {insight.title}
          </h1>
          
          <p className="text-xl text-gray-600 mb-8">
            {insight.excerpt}
          </p>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="font-medium">{insight.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>{formatDate(insight.published_at)}</span>
            </div>
            {insight.views > 0 && (
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                <span>{insight.views} views</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cover Image */}
      {insight.cover_image && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <img
            src={insight.cover_image}
            alt={insight.title}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
        </div>
      )}

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <ReactMarkdown
            components={{
              h2: ({node, ...props}) => <h2 className="text-3xl font-serif text-gray-900 mt-12 mb-6" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-2xl font-serif text-gray-900 mt-8 mb-4" {...props} />,
              p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-6" {...props} />,
              strong: ({node, ...props}) => <strong className="font-semibold text-gray-900" {...props} />,
              ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-6 space-y-2" {...props} />,
              ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-6 space-y-2" {...props} />,
              li: ({node, ...props}) => <li className="text-gray-700" {...props} />,
            }}
          >
            {insight.content}
          </ReactMarkdown>
        </div>

        {/* Tags */}
        {insight.tags && insight.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="h-4 w-4 text-gray-500" />
              {insight.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <h3 className="text-2xl font-serif text-gray-900 mb-4">
            Need Legal Advice on This Topic?
          </h3>
          <p className="text-gray-600 mb-6">
            Contact our team for a confidential consultation
          </p>
          <Button asChild className="bg-gray-900 text-white hover:bg-gray-800">
            <Link to="/contact">
              Get In Touch
            </Link>
          </Button>
        </div>
      </article>
    </div>
  );
};

export default InsightDetail;
